import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { AuthStateModel, OTP_MEANS, UserModel } from '@tsin-core/models/auth.model';
import {
  AuthenticateAction,
  CreateAccountWorkWithUs,
  LogoutAction,
  RefreshTokenAction,
  SetError,
  SetReferralJobSlug,
  ValidateTokenAction
} from '@tsin-core/actions/auth.action';
import { AuthService } from '@tsin-core/services/http/auth.service';
import { catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';

const noAuth = {
  authToken: null,
  token: null,
  id: null,
  email: null,
  isOtpEnabled: false,
  otpMeans: OTP_MEANS.NONE,
  isStaff: false,
  refreshToken: null,
  user: null
};

@State<AuthStateModel>({
  name: 'authState',
  defaults: {
    loading: true,
    auth: noAuth,
    error: null
  }
})
@Injectable()
export class AuthState {
  authService: AuthService = inject(AuthService);

  @Selector()
  static getToken(state: AuthStateModel): string | null {
    return state.auth.token;
  }

  @Selector()
  static getCurrentUser(state: AuthStateModel): UserModel | null {
    return state.auth.user;
  }

  @Selector()
  static getRefreshToken(state: AuthStateModel): string | null {
    return state.auth.refreshToken;
  }

  @Selector()
  static getIsAuthenticated(state: AuthStateModel): boolean {
    return !!state.auth.token;
  }

  @Selector()
  static isLoading(state: AuthStateModel) {
    return state.loading;
  }

  @Selector()
  static getError(state: AuthStateModel) {
    return state.error;
  }

  @Action(ValidateTokenAction)
  validateToken(ctx: StateContext<AuthStateModel>, action: ValidateTokenAction) {
    ctx.patchState({ loading: true });
    return this.authService.validateToken(action.payload).pipe(
      tap((result: UserModel) => {
        console.log('Validate Token Success', result);
        ctx.patchState({
          auth: {
            ...ctx.getState().auth,
            token: action.payload.token,
            refreshToken: action.payload.refreshToken,
            user: result
          },
          loading: false
        });
      }),
      catchError((error) => {
        console.log('Validate Token Error', error);
        ctx.patchState({ loading: false, error: error.message });
        return of(error);
      })
    );
  }


  @Action(RefreshTokenAction)
  refreshToken(ctx: StateContext<AuthStateModel>, action: RefreshTokenAction) {
    ctx.patchState({ loading: true });
    return this.authService.refreshToken(action.payload).pipe(
      tap((result: any) => {
        ctx.patchState({
          auth: {
            ...ctx.getState().auth,
            token: result.accessToken,
          },
          loading: false
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.message });
        throw error;
      })
    );
  }

  @Action(AuthenticateAction)
  validateLogin(ctx: StateContext<AuthStateModel>, action: AuthenticateAction) {
    ctx.patchState({ loading: true });
    console.log('Validate Login Started');
    return this.authService.validate(action.payload).pipe(
      tap((result: any) => {
        console.log('Validate Login Success', result);
        ctx.patchState({
          auth: result,
          loading: false
        });
      }),
      catchError((error) => {
        console.log('Validate Login Error', error);
        ctx.patchState({ loading: false, error: error.message });
        return of(error);
      })
    );
  }

  @Action(LogoutAction)
  logOut(ctx: StateContext<AuthStateModel>, action: LogoutAction) {
    ctx.patchState({ loading: true });
    console.log('Logout Started');
    ctx.patchState({
      auth: undefined,
      loading: false
    });
  }

  @Action(CreateAccountWorkWithUs)
  createAccountWorkWithUs(ctx: StateContext<AuthStateModel>, action: CreateAccountWorkWithUs) {
    ctx.patchState({ loading: true });
    return this.authService.signUpWorkWithUs(action.payload).pipe(
      tap((response) => {
        console.log('Response', response);
        ctx.patchState({ loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false });
        console.log(error);
        throw error;
      })
    );
  }

  @Action(SetReferralJobSlug)
  setReferralJobSlug(ctx: StateContext<AuthStateModel>, action: SetReferralJobSlug) {
    // Reset state to defaults
    ctx.patchState({
      referralJobSlug: action.payload!
    });
  }
}
